import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import { centsToUsd, iso, formatDuration } from '../../lib/format.js';
import { post } from '../../api/client.js';

// Admin home — quick rollups + an action queue. Three buckets:
//   Now / Today / This week — read-only data cards
//   Action queue — clickable rows that route to the underlying surface
export default function DashboardPage() {
  const { data, isLoading } = useDashboard();

  if (isLoading || !data) return <div className="loading">Loading…</div>;
  const {
    live, today, today_hrs,
    jha, week_pay, action_queue, now,
  } = data;

  return (
    <div>
      {/* Top stat row */}
      <div className="sum-row">
        <StatCard label="Clocked in now" value={live.length} />
        <StatCard label={`Hours today (${today})`} value={today_hrs.toFixed(2)} />
        <StatCard
          label="JHA today"
          value={`${jha.submitted} / ${jha.expected}`}
          tone={jha.submitted < jha.expected ? 'warn' : 'good'}
        />
        {week_pay && (
          <StatCard
            label="This week's payroll"
            value={centsToUsd(week_pay.total_cents)}
            sub={`${week_pay.regular_hrs}h reg · ${week_pay.ot_hrs}h OT`}
          />
        )}
      </div>

      {/* Live clocked-in */}
      <Section title="Currently clocked in">
        {live.length === 0 ? (
          <Empty text="No open shifts right now." />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Crew</th><th>Job</th><th>Since</th><th className="num">Elapsed</th>
              </tr>
            </thead>
            <tbody>
              {live.map(s => (
                <tr key={s.id}>
                  <td>{s.user_name}</td>
                  <td>{s.job_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>{new Date(s.clock_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="num">{formatDuration(now - s.clock_in_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* JHA gaps */}
      {jha.missing && jha.missing.length > 0 && (
        <Section title={`Missing JHA today (${jha.missing.length})`}>
          <div className="badge-row">
            {jha.missing.map(m => (
              <span key={m.user_id} className="badge orange">{m.user_name}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Action queue */}
      <Section title="Needs attention">
        <ActionQueue queue={action_queue} now={now} />
      </Section>
    </div>
  );
}

function StatCard({ label, value, sub, tone }) {
  const color = tone === 'warn' ? 'var(--orange)' : 'var(--on-surface)';
  return (
    <div className="sum-card">
      <div className="sum-card-label">{label}</div>
      <div className="sum-card-value" style={{ color }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--warm-gray)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{
        fontFamily: 'var(--font-label)', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.1rem', textTransform: 'uppercase',
        color: 'var(--orange)', marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ color: 'var(--warm-gray)', fontSize: 13, padding: 8 }}>{text}</div>;
}

function ActionQueue({ queue, now }) {
  const items = [];
  const hasStuck = queue.stuck_shifts.length > 0;
  for (const s of queue.stuck_shifts) {
    const hrs = ((now - s.clock_in_at) / 3_600_000).toFixed(1);
    items.push({
      key: 'stuck-' + s.id,
      icon: 'ph-clock-countdown',
      text: <><strong>{s.user_name}</strong> clocked in {hrs}h ago — still open.</>,
      to: '/timesheet',
    });
  }
  for (const d of queue.expiring_docs) {
    const days = Math.ceil((d.expires_at - now) / 86400000);
    items.push({
      key: 'doc-' + d.id,
      icon: 'ph-warning',
      text: <>
        <strong>{d.user_name}</strong>: {d.label || d.kind} —
        {' '}{days <= 0 ? <span style={{ color: 'var(--error-text)' }}>expired</span> : `${days}d left`}.
      </>,
      to: '/admin/documents',
    });
  }
  for (const r of queue.draft_runs) {
    items.push({
      key: 'run-' + r.id,
      icon: 'ph-money',
      text: <>Draft pay run {iso(r.period_start)} → {iso(r.period_end - 1)} pending approval.</>,
      to: `/admin/pay/${r.id}`,
    });
  }
  if (!items.length) return <Empty text="Nothing on fire. ✓" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {hasStuck && <StuckShiftAlertButton />}
      {items.map(it => (
        <Link key={it.key} to={it.to} style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px 12px', background: 'var(--surface-container)',
          color: 'var(--on-surface)', textDecoration: 'none',
          borderLeft: '3px solid var(--orange)',
        }}>
          <i className={'ph ' + it.icon} style={{ color: 'var(--orange)' }} />
          <span style={{ fontSize: 13 }}>{it.text}</span>
        </Link>
      ))}
    </div>
  );
}

// Fires POST /api/admin/check-stuck-shifts — DMs admins + supervising
// foremen (or posts to stuck_shift_channel_id if set in settings).
// Idempotent within a 1h window via clock_entries.last_warned_at.
function StuckShiftAlertButton() {
  const { show } = useError();
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const onClick = async () => {
    setPending(true);
    setResult(null);
    try {
      const body = await post('/api/admin/check-stuck-shifts');
      setResult(body);
    } catch (err) {
      show(err.message);
    } finally {
      setPending(false);
    }
  };
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 12px', background: 'var(--surface-container-low)',
      fontSize: 12, color: 'var(--warm-gray)',
    }}>
      <span>
        <i className="ph ph-bell-ringing" style={{ marginRight: 6 }} />
        {result
          ? `Alerted ${result.alerted} of ${result.scanned} stuck shift${result.scanned === 1 ? '' : 's'} via ${result.mode}.`
          : 'Notify the escalation chain via Discord — admins + supervising foremen (or channel if configured).'}
      </span>
      <button className="btn-sm" type="button" onClick={onClick} disabled={pending}>
        {pending ? 'Sending…' : 'Send alerts now'}
      </button>
    </div>
  );
}
